import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useReferralOptions } from '../../hooks/useReferralOptions';

export interface ReferralAmount {
  referral_id: number;
  amount?: number; // pence — omitted when the row only carries discount text
  discount_text?: string; // shown under the total in the payment summary box
}

interface ReferralAmountsEditorProps {
  /** Stored config: per-referral amounts, keyed by referral id */
  value?: ReferralAmount[];
  onChange: (next?: ReferralAmount[]) => void;
  labelColor?: string;
}

interface Row {
  referralId: number | '';
  pounds: string;
  discountText: string;
}

const toRows = (value?: ReferralAmount[]): Row[] =>
  // Tolerate configs stored in an older shape — they simply start empty
  (Array.isArray(value) ? value : []).map((entry) => ({
    referralId: entry?.referral_id ?? '',
    pounds: entry?.amount != null ? String(entry.amount / 100) : '',
    discountText: entry?.discount_text ?? '',
  }));

const hasAmount = (row: Row) => row.pounds !== '' && !isNaN(parseFloat(row.pounds));

const toConfig = (rows: Row[]): ReferralAmount[] | undefined => {
  const entries = rows
    // A row earns its place with a price, discount text, or both — text-only rows
    // let a referral show discount copy while paying the questionnaire's base price.
    .filter((r) => r.referralId !== '' && (hasAmount(r) || r.discountText.trim() !== ''))
    .map((r) => ({
      referral_id: Number(r.referralId),
      ...(hasAmount(r) ? { amount: Math.round(parseFloat(r.pounds) * 100) } : {}),
      ...(r.discountText.trim() ? { discount_text: r.discountText.trim() } : {}),
    }));
  return entries.length > 0 ? entries : undefined;
};

/**
 * Per-referral price overrides and discount copy for a questionnaire's payment
 * config. Rows are
 * keyed by referral name — the same name the referral's ?referral=<hash> link
 * resolves to — so a questionnaire opened through that link is charged its price
 * instead of the default amount.
 */
export const ReferralAmountsEditor: React.FC<ReferralAmountsEditorProps> = ({
  value,
  onChange,
  labelColor,
}) => {
  const [rows, setRows] = useState<Row[]>(toRows(value));
  const referralOptions = useReferralOptions();

  // Re-sync when a different questionnaire is loaded into the builder
  useEffect(() => {
    setRows(toRows(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const update = (next: Row[]) => {
    setRows(next);
    onChange(toConfig(next));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" sx={{ color: labelColor }}>
        Per-referral amounts and discount text (optional)
      </Typography>
      <Typography variant="caption" sx={{ color: labelColor, opacity: 0.8 }}>
        Overrides the amount above when the questionnaire was opened with that referral's
        link. The discount text is shown under the total in the payment summary box;
        either field on its own is enough.
      </Typography>

      {rows.map((row, index) => (
        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ flex: 2 }}>
            <InputLabel>Referral</InputLabel>
            <Select
              label="Referral"
              value={row.referralId === '' ? '' : String(row.referralId)}
              onChange={(e) =>
                update(
                  rows.map((r, i) =>
                    i === index
                      ? { ...r, referralId: e.target.value === '' ? '' : Number(e.target.value) }
                      : r
                  )
                )
              }
            >
              <MenuItem value="">Select a referral...</MenuItem>
              {referralOptions.map((option) => (
                <MenuItem key={option.id} value={String(option.id)}>
                  {option.name}
                </MenuItem>
              ))}
              {/* Keep a configured referral that is no longer in the list selectable */}
              {row.referralId !== '' && !referralOptions.some((o) => o.id === row.referralId) && (
                <MenuItem value={String(row.referralId)}>Referral #{row.referralId} (not found)</MenuItem>
              )}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label="Amount (£)"
            value={row.pounds}
            onChange={(e) => update(rows.map((r, i) => (i === index ? { ...r, pounds: e.target.value } : r)))}
            inputProps={{ min: 0, step: '0.01' }}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            color="error"
            aria-label="Remove referral amount"
            onClick={() => update(rows.filter((_, i) => i !== index))}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          </Box>
          <TextField
            size="small"
            label="Discount text"
            value={row.discountText}
            onChange={(e) =>
              update(rows.map((r, i) => (i === index ? { ...r, discountText: e.target.value } : r)))
            }
            placeholder="e.g. £30 discount applied"
            fullWidth
          />
        </Box>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setRows([...rows, { referralId: '', pounds: '', discountText: '' }])}
        >
          Add referral price
        </Button>
      </Box>
    </Box>
  );
};

export default ReferralAmountsEditor;
